import React, { useEffect, useState } from 'react';
import { getRooms } from '@/lib/api';
import { ActivitySquare, UsersRound } from 'lucide-react';
import { formatDate, formatNumber } from '@/lib/utils';
import { RoomInspector } from '@/components/rooms/RoomInspector';

export default function RoomsPage() {
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getRooms();
        setRooms(data || []);
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-neutral-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section>
        <h2 className="flex items-center gap-2 text-2xl font-semibold text-neutral-900">
          <ActivitySquare className="h-6 w-6 text-neutral-500" aria-hidden />
          Room Runs
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          Track facilitator-driven sessions, inspect real-time state, and review
          participant performance snapshots.
        </p>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
              <UsersRound className="h-5 w-5 text-neutral-500" aria-hidden />
              Active and historical rooms
            </h3>
            <p className="text-sm text-neutral-500">
              {rooms.length === 0
                ? "No room runs are recorded."
                : `${formatNumber(rooms.length)} total room records.`}
            </p>
          </div>
        </div>

        <div className="mt-5 overflow-hidden rounded-lg border border-neutral-100">
          <table className="min-w-full divide-y divide-neutral-100 text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                  Room code
                </th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                  Quiz
                </th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                  Participants
                </th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                  Started
                </th>
                <th className="px-4 py-3 text-left font-semibold text-neutral-600">
                  Completed
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rooms.length === 0 ? (
                <tr>
                  <td
                    className="px-4 py-5 text-center text-neutral-500"
                    colSpan={5}
                  >
                    Room telemetry is empty. Launch a quiz to populate this
                    table.
                  </td>
                </tr>
              ) : (
                rooms.map((room) => (
                  <tr key={room.id}>
                    <td className="px-4 py-3 font-medium text-neutral-800">
                      {room.room_code}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {room.quiz_name || room.quiz_id}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {formatNumber(room.participants.length)}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {formatDate(room.lifecycle.started_at)}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {formatDate(room.lifecycle.completed_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <RoomInspector />
    </div>
  );
}

